import { computed, useContext } from '@nuxtjs/composition-api';
import { Route } from 'vue-router';
import { isGradeForLocale } from '~/data/grades';
import { FilterInfo, PublicV1FiltersRequest, PublicV1FiltersResponse } from '~/types/api';
import { Locale } from '~/types/locale';
import { isFilteredResourceTypeSlug, yearLevelToTitle } from '~/utils/category';

type FilterLandingPageType = 'resource_type' | 'year_level' | 'unknown';

interface UseFilteredLandingPage {
  slug: string;
  qualifiers: {
    slug: string;
    type: FilterLandingPageType;
    position: number;
  }[];
  yearLevels: string[];
  resourceTypes: string[];
  getFilteredLandingPagePillValues: (
    params: PublicV1FiltersRequest
  ) => Promise<PublicV1FiltersResponse>;
}

const moreThan10Results = (filter: FilterInfo): boolean => filter.count > 10;

const getTypeFromSlug = (slug: string, locale: Locale): FilterLandingPageType => {
  if (isFilteredResourceTypeSlug(slug)) {
    return 'resource_type';
  }
  if (isGradeForLocale(locale, slug)) {
    return 'year_level';
  }

  return 'unknown';
};

export const useFilteredLandingPage = (params: Route['params']): UseFilteredLandingPage => {
  const { i18n, $axios } = useContext();
  const locale = computed(() => i18n.locale as Locale);

  const [slug, ...qualifierSlugs] = params.pathMatch.split('/');

  const qualifiers = qualifierSlugs.map((qualifier, position) => ({
    slug: qualifier,
    type: getTypeFromSlug(qualifier, locale.value),
    position,
  }));

  const yearLevels = qualifiers
    .filter(qualifier => qualifier.type === 'year_level')
    .map(qualifier => qualifier.slug);

  // TODO: For historical reasons some resource types have -<locale> as a suffix.
  // We don't want to retain this for filtered landing pages so we have the URL
  // route include the name without the suffix and then add it when we need to
  // lookup the resources.  We will have to fix this code when we finally rename
  // the resource types so the suffix is excluded.
  const resourceTypes = qualifiers
    .filter(qualifier => qualifier.type === 'resource_type')
    .map(qualifier => {
      return locale.value === 'au' ? qualifier.slug : qualifier.slug + '-' + locale.value;
    });

  const getFilteredLandingPagePillValues = async (
    params: PublicV1FiltersRequest
  ): Promise<PublicV1FiltersResponse> => {
    try {
      const filters = await $axios.$get<PublicV1FiltersResponse>('/public/v1/filter', {
        params: {
          ...params,
          year_levels: [],
          resource_types: [],
        },
      });
      // HACK - remap grades for us locale
      // TODO replace with correct names
      if (i18n.locale === 'us' && filters.year_levels) {
        return {
          ...filters,
          year_levels: filters.year_levels?.map((yearLevel: FilterInfo) => ({
            ...yearLevel,
            name: yearLevelToTitle('us', yearLevel.slug),
          })),
        };
      }

      return {
        resource_types:
          filters.resource_types
            ?.filter(moreThan10Results)
            ?.filter(({ slug }) => isFilteredResourceTypeSlug(slug)) || [],
        year_levels:
          filters.year_levels
            ?.filter(moreThan10Results)
            ?.map(info => ({ ...info, name: 'for ' + info.name })) || [],
      };
    } catch (err) {
      console.error(err);
    }

    return {
      resource_types: [],
      year_levels: [],
    };
  };

  return {
    slug,
    qualifiers,
    yearLevels,
    resourceTypes,
    getFilteredLandingPagePillValues,
  };
};
